import React, { useState } from 'react';
import { CheckCircle, XCircle, RotateCcw, Brain, Circle } from 'lucide-react';

const DrillQuestions = ({ drillQuestions, onComplete, onNewDrill }) => {
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState(new Set());

  const handleAnswerSelect = (questionId, selectedOption) => {
    if (showResults) return; // Prevent changes after results are shown
    
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: selectedOption
    }));
    
    // Track answered questions for progress
    setAnsweredQuestions(prev => new Set([...prev, questionId]));
  };

  const handleSubmit = () => {
    let correct = 0;
    drillQuestions.forEach(question => {
      const selected = selectedAnswers[question.id];
      if (selected === question.correctAnswer) {
        correct++;
      }
    });
    
    setCorrectCount(correct);
    setShowResults(true);
  };

  const handleNewDrill = () => {
    setSelectedAnswers({});
    setShowResults(false);
    setCorrectCount(0);
    setAnsweredQuestions(new Set());
    onNewDrill();
  };

  const getAnswerStatus = (questionId, optionLetter) => {
    const question = drillQuestions.find(q => q.id === questionId);
    const selected = selectedAnswers[questionId];
    
    // Show immediate feedback when answer is selected
    if (selected === optionLetter) {
      if (optionLetter === question.correctAnswer) {
        return 'correct';
      } else {
        return 'incorrect';
      }
    }
    
    // Show correct answer after any answer is selected
    if (answeredQuestions.has(questionId) && optionLetter === question.correctAnswer) {
      return 'correct';
    }
    
    return 'default';
  };

  const getAnswerStyles = (status) => {
    switch (status) {
      case 'correct':
        return 'bg-green-500 text-white border-green-500';
      case 'incorrect':
        return 'bg-red-500 text-white border-red-500';
      default:
        return 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50';
    }
  };

  const getProgressStatus = (questionId) => {
    if (answeredQuestions.has(questionId)) {
      const question = drillQuestions.find(q => q.id === questionId);
      const selected = selectedAnswers[questionId];
      return selected === question.correctAnswer ? 'correct' : 'incorrect';
    }
    return 'pending';
  };

  const allQuestionsAnswered = drillQuestions.every(q => selectedAnswers[q.id]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Brain className="w-6 h-6 text-purple-600" />
          <h3 className="text-xl font-bold text-gray-800">AI 練習題</h3>
        </div>
        <button
          onClick={handleNewDrill}
          className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          <span>新練習題</span>
        </button>
      </div>

      {/* Progress Indicator */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">進度</span>
          <span className="text-sm text-gray-600">
            {answeredQuestions.size} / {drillQuestions.length} 題
          </span>
        </div>
        <div className="flex space-x-2">
          {drillQuestions.map((question) => {
            const status = getProgressStatus(question.id);
            return (
              <div
                key={question.id}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200 ${
                  status === 'correct' 
                    ? 'bg-green-500 text-white' 
                    : status === 'incorrect'
                    ? 'bg-red-500 text-white'
                    : 'bg-gray-300 text-gray-600'
                }`}
              >
                {status === 'correct' && <CheckCircle className="w-4 h-4" />}
                {status === 'incorrect' && <XCircle className="w-4 h-4" />}
                {status === 'pending' && question.id}
              </div>
            );
          })}
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-6">
        {drillQuestions.map((question) => (
          <div key={question.id} className="bg-white rounded-lg border p-6 shadow-sm">
            <div className="mb-4">
              <span className="inline-block bg-purple-100 text-purple-800 text-sm font-medium px-2 py-1 rounded mr-2">
                練習題 {question.id}
              </span>
              <span className="text-gray-700 font-medium">{question.question}</span>
            </div>

            {/* Options */}
            <div className="space-y-3 mb-4">
              {question.options.map((option) => {
                const status = getAnswerStatus(question.id, option.letter);
                const isSelected = selectedAnswers[question.id] === option.letter;
                
                return (
                  <button
                    key={option.letter}
                    onClick={() => handleAnswerSelect(question.id, option.letter)}
                    disabled={showResults || answeredQuestions.has(question.id)}
                    className={`flex items-center justify-start w-full p-4 rounded-lg border-2 transition-all duration-200 ${
                      getAnswerStyles(status)
                    } ${
                      isSelected && !showResults ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                    }`}
                  >
                    <span className="font-medium min-w-[2rem]">{option.letter}.</span>
                    <span className="ml-3 flex-1 text-left">{option.text}</span>
                    {status === 'correct' && <CheckCircle className="w-5 h-5 ml-2 flex-shrink-0" />}
                    {status === 'incorrect' && <XCircle className="w-5 h-5 ml-2 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>

            {/* Explanation - Show immediately after answer is selected */}
            {answeredQuestions.has(question.id) && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <span className="inline-block bg-blue-100 text-blue-800 text-sm font-medium px-2 py-1 rounded mr-2">
                  解釋
                </span>
                <p className="text-gray-700 text-sm mt-1">{question.explanation}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Submit Button */}
      {!showResults && (
        <div className="text-center">
          <button
            onClick={handleSubmit}
            disabled={!allQuestionsAnswered}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              allQuestionsAnswered
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            查看總結果
          </button>
        </div>
      )}

      {/* Results */}
      {showResults && (
        <div className="bg-white rounded-lg border p-6 shadow-sm">
          <div className="text-center">
            <h4 className="text-lg font-bold text-gray-800 mb-2">練習結果</h4>
            <div className="text-3xl font-bold text-purple-600 mb-2">
              {correctCount} / {drillQuestions.length}
            </div>
            <p className="text-gray-600 mb-4">
              正確率: {Math.round((correctCount / drillQuestions.length) * 100)}%
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={handleNewDrill}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                再練習一次
              </button>
              <button
                onClick={onComplete}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                完成練習
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DrillQuestions; 